import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  ScanLine,
  FileText,
  Send,
  CheckCircle2,
  CreditCard,
  TrendingUp,
  Circle,
} from "lucide-react";

const KIND_META = {
  scan: { icon: ScanLine, color: "text-[#D5FF3F]", ring: "border-[#D5FF3F]/30 bg-[#D5FF3F]/5" },
  dispute_drafted: { icon: FileText, color: "text-[#7CFF9B]", ring: "border-[#7CFF9B]/30 bg-[#7CFF9B]/5" },
  mail_sent: { icon: Send, color: "text-[#D5FF3F]", ring: "border-[#D5FF3F]/40 bg-[#D5FF3F]/10" },
  mail_delivered: { icon: CheckCircle2, color: "text-[#D5FF3F]", ring: "border-[#D5FF3F] bg-[#D5FF3F]/20" },
  tradeline_purchased: { icon: CreditCard, color: "text-[#7CFF9B]", ring: "border-[#7CFF9B]/40 bg-[#7CFF9B]/10" },
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export function ProgressTimeline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/progress/timeline");
        setData(r.data);
      } catch (err) {
        console.warn("timeline load", err?.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data) return null;
  const events = data.events || [];

  const lo = data.expected_lift_range?.low || 0;
  const hi = data.expected_lift_range?.high || 0;

  return (
    <div className="card-surface p-6 mb-6" data-testid="progress-timeline">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="overline mb-2">Score journey</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight">
            Every move you&rsquo;ve made.
          </h2>
          <p className="text-sm text-[#A8ADA5] mt-1.5 max-w-lg">
            {events.length === 0
              ? "Your timeline will start filling in the moment you scan your first report."
              : `${events.length} action${events.length === 1 ? "" : "s"} completed · projected FICO lift so far ${lo > 0 || hi > 0 ? `+${lo} to +${hi} pts` : "pending USPS delivery"}`}
          </p>
        </div>
        {(lo > 0 || hi > 0) && (
          <div className="text-right">
            <div className="overline mb-1">Est. total lift</div>
            <div className="ticker text-4xl text-[#D5FF3F]" data-testid="timeline-total-lift">
              +{lo}
              <span className="text-[#6D716C] text-2xl"> to </span>
              +{hi}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C]">pts (projected)</div>
          </div>
        )}
      </div>

      {events.length === 0 ? (
        <div className="text-center py-8">
          <Circle size={22} className="text-[#6D716C] mx-auto mb-3" />
          <div className="text-sm text-[#A8ADA5]">Nothing yet — go run your first scan.</div>
        </div>
      ) : (
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[15px] top-2 bottom-2 w-px bg-[#26282C]" />
          <div className="space-y-4">
            {events.map((e, i) => {
              const meta = KIND_META[e.kind] || KIND_META.scan;
              const Icon = meta.icon;
              return (
                <div
                  key={`${e.kind}-${e.at}-${i}`}
                  data-testid={`timeline-event-${e.kind}`}
                  className="flex items-start gap-4 relative"
                >
                  <div
                    className={`w-8 h-8 rounded-sm flex-shrink-0 flex items-center justify-center border-2 relative z-10 ${meta.ring}`}
                  >
                    <Icon size={13} className={meta.color} strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="text-xs font-mono uppercase tracking-widest text-[#6D716C] mb-0.5">
                      {fmtDate(e.at)}
                    </div>
                    <div className="text-sm font-medium text-[#F5F3EE] leading-tight">
                      {e.title}
                    </div>
                    {e.detail && (
                      <div className="text-xs text-[#A8ADA5] mt-1 line-clamp-1">{e.detail}</div>
                    )}
                    {(e.expected_lift_lo > 0 || e.expected_lift_hi > 0) && (
                      <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-mono text-[#D5FF3F]">
                        <TrendingUp size={10} /> +{e.expected_lift_lo}–{e.expected_lift_hi} pts est.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.current_score != null && data.target_score != null && (
        <div className="mt-6 pt-5 border-t border-[#26282C]">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-mono uppercase tracking-widest text-[#A8ADA5]">Score</div>
            <div className="ticker text-sm">
              <span className="text-[#F5F3EE]">{data.current_score}</span>
              <span className="text-[#6D716C] mx-2">→</span>
              <span className="text-[#D5FF3F]">{data.target_score}</span>
            </div>
          </div>
          <div className="h-2 bg-[#131518] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#7CFF9B] to-[#D5FF3F]"
              style={{
                width: `${Math.min(100, Math.max(2, (data.current_score / (data.target_score || 850)) * 100))}%`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
